import React, { useState } from 'react';
import { BarChart2, Calculator, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели быстрого расчета
 */
const QuickCalculationSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  setResults
}) => {
  // Состояние для быстрого расчета
  const [quickCalcValues, setQuickCalcValues] = useState({
    length: parseFloat(formData.length) || 0,
    width: parseFloat(formData.width) || 0,
    height: parseFloat(formData.ceilingHeight) || 0
  });
  
  const [quickResult, setQuickResult] = useState(null);
  
  // Обработчик изменения значений быстрого расчета
  const handleQuickCalcChange = (field, value) => {
    setQuickCalcValues({
      ...quickCalcValues,
      [field]: value
    });
  };
  
  // Быстрый расчет площади и других параметров
  const calculateQuickResult = () => {
    const { length, width, height } = quickCalcValues;
    
    const floorArea = length * width;
    const wallArea = 2 * (length + width) * height;
    const ceilingArea = floorArea;
    const perimeter = 2 * (length + width);
    const volume = floorArea * height;
    
    setQuickResult({
      floorArea,
      wallArea,
      ceilingArea,
      perimeter,
      volume
    });
  };
  
  // Применение быстрого расчета к основной форме
  const applyQuickCalc = () => {
    setFormData({
      ...formData,
      roomShape: 'rectangle',
      length: quickCalcValues.length.toString(),
      width: quickCalcValues.width.toString(),
      ceilingHeight: quickCalcValues.height.toString()
    });
    
    // Рассчитываем полный результат для отображения в основной части
    if (quickResult) {
      setResults({
        floorArea: quickResult.floorArea,
        wallPerimeter: quickResult.perimeter,
        wallArea: quickResult.wallArea,
        ceilingArea: quickResult.ceilingArea,
        totalArea: quickResult.floorArea + quickResult.wallArea + quickResult.ceilingArea,
        balconyArea: 0,
        openingsArea: 0,
        skirtingLength: quickResult.perimeter,
        ceilingHeight: quickCalcValues.height,
        flooringCost: quickResult.floorArea * parseFloat(formData.flooringUnitCost || 0),
        wallMaterialCost: quickResult.wallArea * parseFloat(formData.wallMaterialUnitCost || 0),
        ceilingMaterialCost: quickResult.ceilingArea * parseFloat(formData.ceilingUnitCost || 0),
        totalMaterialCost: 
          quickResult.floorArea * parseFloat(formData.flooringUnitCost || 0) +
          quickResult.wallArea * parseFloat(formData.wallMaterialUnitCost || 0) +
          quickResult.ceilingArea * parseFloat(formData.ceilingUnitCost || 0),
        measurementUnit: formData.measurementUnit,
        roomShape: 'rectangle'
      });
    }
  };
  
  // Предопределенные типовые размеры для комнат
  const commonRoomSizes = [
    { name: 'Маленькая', length: 3, width: 2.5, height: 2.5 },
    { name: 'Средняя', length: 4, width: 3, height: 2.7 },
    { name: 'Большая', length: 5, width: 4, height: 2.8 },
    { name: 'Кухня', length: 3, width: 2.8, height: 2.7 },
    { name: 'Ванная', length: 2, width: 1.8, height: 2.6 },
    { name: 'Коридор', length: 4, width: 1.5, height: 2.7 }
  ];
  
  // Обработчик выбора типового размера
  const selectRoomSize = (size) => {
    setQuickCalcValues({
      length: size.length,
      width: size.width,
      height: size.height
    });
    
    // Автоматически рассчитываем результат
    const floorArea = size.length * size.width;
    const wallArea = 2 * (size.length + size.width) * size.height;
    const ceilingArea = floorArea;
    const perimeter = 2 * (size.length + size.width);
    const volume = floorArea * size.height;
    
    setQuickResult({
      floorArea,
      wallArea,
      ceilingArea,
      perimeter,
      volume
    });
  };
  
  const unit = formData.measurementUnit === 'meters' ? 'м' : 
               formData.measurementUnit === 'centimeters' ? 'см' : 'мм';
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-fuchsia-500" />
        Быстрый расчет площади
      </h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="quickLength" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Длина ({unit})
          </label>
          <input
            type="number"
            id="quickLength"
            value={quickCalcValues.length}
            onChange={(e) => handleQuickCalcChange('length', parseFloat(e.target.value) || 0)}
            className="form-input w-full"
            min="0"
            step="0.1"
          />
        </div>
        
        <div>
          <label htmlFor="quickWidth" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Ширина ({unit})
          </label>
          <input
            type="number"
            id="quickWidth"
            value={quickCalcValues.width}
            onChange={(e) => handleQuickCalcChange('width', parseFloat(e.target.value) || 0)}
            className="form-input w-full"
            min="0"
            step="0.1"
          />
        </div>
        
        <div>
          <label htmlFor="quickHeight" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Высота потолка ({unit})
          </label>
          <input
            type="number"
            id="quickHeight"
            value={quickCalcValues.height}
            onChange={(e) => handleQuickCalcChange('height', parseFloat(e.target.value) || 0)}
            className="form-input w-full"
            min="0"
            step="0.1"
          />
        </div>
        
        <div>
          <Button
            primary
            fullWidth
            onClick={calculateQuickResult}
            icon={<BarChart2 className="w-4 h-4" />}
          >
            Рассчитать
          </Button>
        </div>
        
        {quickResult && (
          <div className="mt-4 p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800">
            <h4 className="text-sm font-medium text-fuchsia-700 dark:text-fuchsia-300 mb-2">
              Результат расчета:
            </h4>
            <div className="space-y-2 text-fuchsia-600 dark:text-fuchsia-400 text-sm">
              <div className="flex justify-between">
                <span>Площадь пола:</span>
                <span className="font-medium">{formatNumber(quickResult.floorArea, 2)} {unit}²</span>
              </div>
              <div className="flex justify-between">
                <span>Площадь стен:</span>
                <span className="font-medium">{formatNumber(quickResult.wallArea, 2)} {unit}²</span>
              </div>
              <div className="flex justify-between">
                <span>Периметр комнаты:</span>
                <span className="font-medium">{formatNumber(quickResult.perimeter, 2)} {unit}</span>
              </div>
              <div className="flex justify-between">
                <span>Объем помещения:</span>
                <span className="font-medium">{formatNumber(quickResult.volume, 2)} {unit}³</span>
              </div>
            </div>
            
            <div className="mt-4">
              <Button
                onClick={applyQuickCalc}
                fullWidth
                variant="secondary"
                size="sm"
                icon={<ArrowRight className="w-4 h-4" />}
              >
                Применить к форме
              </Button>
            </div>
          </div>
        )}
        
        <div className="mt-2">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Типовые размеры комнат:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {commonRoomSizes.map((size, index) => (
              <button
                key={index}
                onClick={() => selectRoomSize(size)}
                className="px-2 py-1 text-xs rounded border transition-colors
                  bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 
                  dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {size.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickCalculationSidebar;